import React, { useState, useEffect } from 'react';
import { Table, TableCellProps, TablePagination } from '@mui/material';

// eslint-disable-next-line import/no-cycle
import ZerpaTableHeader, { ZerpaTableHeaderProps } from './ZerpaTableHeader';
// eslint-disable-next-line import/no-cycle
import ZerpaTableBody, { ZerpaTableBodyProps, RowProperty } from './ZerpaTableBody';
import { CheckboxSelection } from './useCheckbox';

import { sortByString, sortByDate } from '../../utilities/Sorting';

import { ZerpaState } from '../../../redux/types';

type ColumnType = 'checkbox' | 'rating';
export type ColumnObject = {
  text: string | React.ReactNode;
  keyName: string;
  sortable?: boolean;
  format?: {
    align: TableCellProps['align'];
  };
  isDate?: boolean;
  type?: ColumnType;
}

/** Expose more internal state setters here if required */
export type ZerpaTableStateSetters = {
  setPage: React.Dispatch<React.SetStateAction<number>>;
}

export interface ZerpaTableConfig<T> extends ZerpaTableBodyProps<T>, ZerpaTableHeaderProps {
  dense?: boolean;
  showPagination?: boolean;
  checkboxSelection?: CheckboxSelection;
  getZerpaState?: (
    zerpaState: ZerpaState,
    stateSetters?: ZerpaTableStateSetters,
  ) => void;
  zerpaState?: ZerpaState;
  color?: 'green';
}

export function PureZerpaTable<T extends { [P in keyof T]: T[P] }>(
  props: ZerpaTableConfig<T>,
): JSX.Element {
  const {
    tableRows = [],
    columns,
    keyIdentifier,
    showPagination = false,
    dense,
    checkboxSelection,
    zerpaState,
    renderOnSelected = undefined,
    rowClick,
    getZerpaState,
    multiselect = true,
    color,
  } = props;
  const [selectedRow, setSelectedRow] = useState<RowProperty>(zerpaState?.selectedRow || false);
  const [orderBy, setOrderBy] = useState<string>(zerpaState?.orderBy || '');
  const [columnIsDate, setColumnIsDate] = useState<boolean>(zerpaState?.columnIsDate || false);
  const [order, setOrder] = useState<false | 'desc' | 'asc' | undefined>(zerpaState?.order || 'asc');
  const [page, setPage] = useState<number>(zerpaState?.page || 0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(zerpaState?.rowsPerPage || 50);

  /** send local state updates to parent's callback if exists */
  useEffect(() => {
    if (getZerpaState) {
      const zerpaCurrentState = {
        columnIsDate,
        order,
        orderBy,
        page,
        rowsPerPage,
        selectedRow,
      } as ZerpaState;
      getZerpaState(zerpaCurrentState, { setPage } as ZerpaTableStateSetters);
    }
  }, [
    columnIsDate,
    order,
    orderBy,
    page,
    rowsPerPage,
    selectedRow,
    getZerpaState,
  ]);

  const handleRequestSort = (columnObj: ColumnObject): void => {
    const isDesc = orderBy === columnObj.keyName && order === 'desc';
    setOrder(isDesc ? 'asc' : 'desc');
    setOrderBy(columnObj.keyName);
    setColumnIsDate(!!columnObj.isDate);
  };

  const handlePageChange = (event: unknown, pageNumber: number): void => {
    setPage(pageNumber);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  let processedRows = tableRows;
  // sort
  if (columnIsDate) {
    processedRows = tableRows.sort((a, b) => sortByDate(order, orderBy)(a, b));
  } else {
    processedRows = tableRows.sort((a, b) => sortByString(order, orderBy)(a, b));
  }

  // slice for pagination
  if (showPagination) {
    const startRow = page * rowsPerPage;
    const endRow = startRow + rowsPerPage;
    processedRows = processedRows.slice(startRow, endRow);
  }

  return (
    <div>
      <Table className="zerpa-table" size={dense ? 'small' : 'medium'}>
        <ZerpaTableHeader
          columns={columns}
          onColumnClick={handleRequestSort}
          orderBy={orderBy}
          order={order}
          color={color}
        />
        <ZerpaTableBody
          tableRows={processedRows}
          columns={columns}
          keyIdentifier={keyIdentifier}
          onRowClick={(key): void => setSelectedRow(key === selectedRow ? false : key)}
          rowClick={rowClick}
          multiselect={multiselect}
          renderOnSelected={renderOnSelected}
          selectedRow={selectedRow}
          checkedRows={checkboxSelection?.selectedIds}
          setCheckedRows={checkboxSelection?.setSelectedIds}
          color={color}
        />
      </Table>
      {
        showPagination && (
          <TablePagination
            component="div"
            count={tableRows.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        )
      }
    </div>
  );
}

export default (PureZerpaTable);
